import { Button, type ButtonProps } from '@ctw/shared/components/button';
import { notify } from '@ctw/shared/components/toast';
import { Tooltip, type TooltipProps } from '@ctw/shared/components/tooltip';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import {
  type FunctionComponent,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useRef,
} from 'react';

export interface CopyToClipboardProps extends PropsWithChildren {
  content?: string;
  className?: ClassName;
}

const withHiddenTextArea = <T,>(callback: (textArea: HTMLTextAreaElement) => T): T => {
  const textArea = document.createElement('textarea');
  textArea.style.top = `${document.documentElement.scrollTop}`;
  textArea.style.left = `${document.documentElement.scrollLeft}`;
  textArea.style.position = 'fixed';
  document.body.appendChild(textArea);

  const returnValue = callback(textArea);

  document.body.removeChild(textArea);

  return returnValue;
};

const isFeaturePolicyClipboardWriteEnabled = (): boolean => {
  const doc = document as unknown as {
    featurePolicy?: { allowsFeature: (feature: string) => boolean };
  };

  return doc.featurePolicy ? doc.featurePolicy.allowsFeature('clipboard-write') : false;
};

const isNavigatorClipboardAvailable = (): Promise<boolean> => {
  try {
    if (!navigator.clipboard?.writeText) {
      return Promise.resolve(false);
    }

    return new Promise((resolve, _reject) => {
      navigator.clipboard
        .writeText(' ')
        .then(() => resolve(true))
        .catch(() => resolve(false));
    });
  } catch (_err) {
    return Promise.resolve(false);
  }
};

const isDocumentCopyActionAvailable = (): boolean =>
  withHiddenTextArea((textArea) => {
    textArea.value = ' ';
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');

      if (successful === true) {
        return true;
      }
      return false;
    } catch (_err) {
      return false;
    }
  });

export const copyToClipboard = async (content: string, afterCopy?: () => void) => {
  if (isFeaturePolicyClipboardWriteEnabled() && (await isNavigatorClipboardAvailable())) {
    try {
      void navigator.clipboard.writeText(content).then(() => afterCopy?.());
    } catch (_error) {
      /* noop */
    }
  } else if (isDocumentCopyActionAvailable()) {
    const success = withHiddenTextArea((textArea) => {
      textArea.value = content;
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');

        if (successful === true) {
          return true;
        }
        return false;
      } catch (_err) {
        return false;
      }
    });

    if (success && afterCopy) {
      afterCopy();
    }
  } else {
    notify({
      type: 'error',
      title: 'Copy to clipboard is not supported in this web browser',
    });
  }
};

export const CopyToClipboard = ({ content, children, className }: CopyToClipboardProps) => {
  const contentElem = useRef<HTMLDivElement>(null);

  const onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const textContent = content ? content : (contentElem.current?.innerText ?? '');

      void copyToClipboard(textContent, () => {
        notify({
          type: 'info',
          title: `Copied to clipboard: '${textContent}'`,
        });
      });
    },
    [content],
  );

  return (
    <div className={twx('group flex items-center gap-1', className)}>
      <div className={tw`text-left`} ref={contentElem}>
        {children}
      </div>
      <CopyToClipboardButtonComponent onClick={onClick} />
    </div>
  );
};

// CopyButton props are mapped to its underlying components: tooltipId => TooltipProps['id'], etc
export type CopyToClipboardButtonComponentProps = {
  // we allow content and id to be optional because we provide defaults in the CopyButton
  [K in keyof Pick<
    TooltipProps,
    'content' | 'id' | 'enabled'
  > as `tooltip${Capitalize<K>}`]?: TooltipProps[K];
} & {
  [K in keyof Pick<
    ButtonProps,
    'ariaLabel' | 'disabled' | 'size' | 'className'
  > as `button${Capitalize<K>}`]: ButtonProps[K];
} & {
  [K in keyof Pick<
    FontAwesomeIconProps,
    'className'
  > as `icon${Capitalize<K>}`]: FontAwesomeIconProps[K];
  // Common conventions dictate that we assign core behavior to certain child components.
} & { onClick: ButtonProps['onClick'] };

/**
 * A display-only copy-button component with reasonable defaults.
 * A copy button is composed of a button, a tooltip, and an icon.
 */
export const CopyToClipboardButtonComponent: FunctionComponent<
  CopyToClipboardButtonComponentProps
> = ({
  tooltipContent = 'Copy to Clipboard',
  tooltipId = 'copy-to-clipboard',
  tooltipEnabled,
  buttonAriaLabel,
  buttonSize,
  buttonDisabled,
  buttonClassName,
  onClick,
  iconClassName = tw`h-4 w-4 cursor-pointer text-content-icon opacity-0 transition-opacity group-hover:opacity-100`,
}) => (
  <Button
    onClick={onClick}
    type="button"
    ariaLabel={buttonAriaLabel}
    size={buttonSize}
    disabled={buttonDisabled}
    variant="unstyled"
    telemetryObfuscatedIdentifier="COPY TO CLIPBOARD"
    className={buttonClassName}
  >
    <Tooltip id={tooltipId} content={tooltipContent} enabled={tooltipEnabled}>
      <FontAwesomeIcon icon={faCopy} className={iconClassName} />
    </Tooltip>
  </Button>
);
